// ========================================
// CHATBOT WIDGET - MAIN MODULE
// Refactored Version using Modular Architecture
// 
// Dependencies:
// - chatbot-config.js
// - chatbot-ui.js
// - chatbot-view-controller.js
// - chatbot-data-manager.js
// - chatbot-questions.js
// - chatbot-messages.js
// - chatbot-products.js
// ========================================

(function () {
  'use strict';

  document.addEventListener("DOMContentLoaded", function () {

    // Ensure Config is loaded
    if (typeof window.ChatbotConfig === 'undefined') {
      console.error("ChatbotConfig not loaded! Check script include order.");
      return;
    }

    const CONFIG = window.ChatbotConfig;
    const DataManager = window.ChatbotDataManager;
    const ViewController = window.ChatbotViewController;

    // Get user information
    let CUSTOMER_ID = null;
    let CUSTOMER_EMAIL = null;
    let CUSTOMER_NAME = null;

    try {
      CUSTOMER_ID = (typeof window.SHOPIFY_CUSTOMER_ID !== 'undefined' && window.SHOPIFY_CUSTOMER_ID !== null)
        ? window.SHOPIFY_CUSTOMER_ID.toString()
        : null;
      CUSTOMER_EMAIL = (typeof window.SHOPIFY_CUSTOMER_EMAIL !== 'undefined' && window.SHOPIFY_CUSTOMER_EMAIL !== null && window.SHOPIFY_CUSTOMER_EMAIL !== 'null')
        ? window.SHOPIFY_CUSTOMER_EMAIL.trim()
        : null;
      CUSTOMER_NAME = (typeof window.SHOPIFY_CUSTOMER_NAME !== 'undefined' && window.SHOPIFY_CUSTOMER_NAME !== null && window.SHOPIFY_CUSTOMER_NAME !== 'null')
        ? window.SHOPIFY_CUSTOMER_NAME.trim()
        : null;

    } catch (e) {
      // Error reading customer data
    }

    // User Info Object
    const USER_INFO = {
      id: CUSTOMER_ID,
      email: CUSTOMER_EMAIL,
      name: CUSTOMER_NAME
    };

    // ========================================
    // INITIALIZATION
    // ========================================
    if (typeof window.ChatbotUI === 'undefined') {
      console.error("ChatbotUI not loaded!");
      return;
    }

    // Init UI and View Controller
    window.ChatbotUI.init();
    if (ViewController) ViewController.init();

    const DOM = window.ChatbotUI.getRefs();

    // ========================================
    // STATE
    // ========================================
    let questionnaireCompleted = false;
    let activeQuestionnaireData = null;
    let isRetakingTest = false;
    let canShowQuestionnaire = true;
    let lastGuidedProductId = null; // Track to prevent duplicate welcome messages
    const LAST_GUIDED_PRODUCT_STORAGE_KEY = 'codeskin_chatbot_last_guided_product_id';
    let forceProductsVideoOnNextResults = false; // Retake flows should replay products intro video
    let productClickInProgress = false; // Guard against double-click race

    // ========================================
    // LOGIC
    // ========================================

    async function loadDataAndInit() {
      // Pre-fetch questionnaire data
      if (DataManager) {
        activeQuestionnaireData = await DataManager.loadQuestionnaireData();
      }
    }

    async function checkStatus() {
      if (!DataManager) return;

      // Don't check if retaking
      if (isRetakingTest) return;

      const status = await DataManager.checkQuestionnaireStatus(USER_INFO);

      canShowQuestionnaire = status.canShowQuestionnaire;

      // If we have recommendations/cached data
      if (status.recommendations) {
        DataManager.setCachedRecommendations(status.recommendations);
        showChatInterface(status.recommendations);
      } else if (!canShowQuestionnaire) {
        // Cannot show questionnaire. If recommendations are missing (or not hydrated yet),
        // do NOT force an empty results layout on refresh.
        // Show welcome/fallback so lower panel still has video/content.
        const cachedRecs = DataManager.getCachedRecommendations();
        const hasGuided =
          typeof ChatbotMessages !== 'undefined' &&
          typeof ChatbotMessages.hasPersistedGuidedActivity === 'function' &&
          ChatbotMessages.hasPersistedGuidedActivity();
        if (cachedRecs || hasGuided) {
          showChatInterface(cachedRecs);
        } else {
          showWelcomeScreen();
        }
      } else {
        // API says questionnaire is available — still resume results if we have local cache
        // (recommendations + guided chat are persisted across reload / close-open).
        const cachedRecs = DataManager.getCachedRecommendations();
        const hasGuided =
          typeof ChatbotMessages !== 'undefined' &&
          typeof ChatbotMessages.hasPersistedGuidedActivity === 'function' &&
          ChatbotMessages.hasPersistedGuidedActivity();
        if (cachedRecs || hasGuided) {
          showChatInterface(cachedRecs);
        } else if (!questionnaireCompleted) {
          showWelcomeScreen();
        }
      }
    }

    function showWelcomeScreen() {
      if (window.ChatbotUI && typeof window.ChatbotUI.setHeaderTitle === 'function') {
        window.ChatbotUI.setHeaderTitle('Codeskin');
      }
      if (ViewController) {
        ViewController.showWelcomeScreen({
          hasRecommendations: !!DataManager.getCachedRecommendations(),
          canShowQuestionnaire: canShowQuestionnaire,
          onStart: startFaceScan,
          onView: () => showChatInterface(DataManager.getCachedRecommendations()),
          onRetake: retakeTest
        });
      }
    }

    function toggleChatbot() {
      if (!ViewController) return;

      // Auth gate: only authenticated users can open chatbot.
      const isAuthed = !!(window.CHATBOT_IS_AUTHENTICATED || window.SHOPIFY_CUSTOMER_ID);
      if (!isAuthed) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
        const authBase = window.CHATBOT_AUTH_REQUIRED_URL || (root + 'account/login');
        const returnUrl = window.location.pathname + window.location.search;
        const sep = authBase.indexOf('?') >= 0 ? '&' : '?';
        window.location.href = authBase + sep + 'return_url=' + encodeURIComponent(returnUrl);
        return;
      }

      const isOpen = ViewController.toggleChatbot();

      if (!isOpen) {
        ViewController.stopWelcomeVideo();
      }

      if (isOpen && !isRetakingTest) {
        checkStatus();
      }
    }

    async function retakeTest() {
      console.log("[Chatbot] Initializing Fresh Retake (Full)...");
      isRetakingTest = true;
      questionnaireCompleted = false; // Retake must not shortcut back to products
      canShowQuestionnaire = true; // FORCE Questionnaire to follow face scan
      activeQuestionnaireData = null; // Clear old data to force refresh
      forceProductsVideoOnNextResults = true;
      lastGuidedProductId = null;
      try { localStorage.removeItem(LAST_GUIDED_PRODUCT_STORAGE_KEY); } catch (e) { /* ignore */ }

      // Reset Questionnaire Module
      if (typeof ChatbotQuestionnaire !== 'undefined') {
        ChatbotQuestionnaire.resetQuestionnaire();
      }

      // Reset View
      if (ViewController) ViewController.resetViewForRetake();
      if (typeof ChatbotMessages !== 'undefined' && ChatbotMessages.clearGuidedOptions) {
        ChatbotMessages.clearGuidedOptions();
      }
      if (DataManager && typeof DataManager.clearAllCachedState === 'function') {
        DataManager.clearAllCachedState();
      } else if (DataManager && typeof DataManager.setCachedRecommendations === 'function') {
        // Backward fallback if clearAllCachedState is unavailable.
        DataManager.setCachedRecommendations(null);
      }
      // Clear routine variant memory BEFORE cart sync — otherwise getRoutineProductsInCartCount
      // still uses the previous session's variant set and can show a false "items in bag" count.
      if (window.ChatbotProducts && typeof window.ChatbotProducts.clearRuntimeState === 'function') {
        window.ChatbotProducts.clearRuntimeState();
      }
      if (window.ChatbotProducts && typeof window.ChatbotProducts.syncStorefrontCartCount === 'function') {
        window.ChatbotProducts.syncStorefrontCartCount();
      }

      // Prefetch fresh data in background while face scan happens
      if (DataManager) {
        DataManager.loadQuestionnaireData(true).then(data => {
          activeQuestionnaireData = data;
          console.log("[Chatbot] Fresh questionnaire data loaded in background.");
        });
      }

      // Start with Face Scan
      if (typeof startFaceScan === 'function') startFaceScan();

      // Show "Back to Results" if we have them
      if (DOM.backToResultsBtn) {
        const cached = DataManager ? DataManager.getCachedRecommendations() : null;
        DOM.backToResultsBtn.style.display = cached ? 'block' : 'none';
        DOM.backToResultsBtn.onclick = () => {
          isRetakingTest = false;
          showChatInterface(cached);
        };
      }
    }

    async function retakeQuestionnaire() {
      console.log("[Chatbot] Initializing Fresh Retake (Questionnaire Only)...");
      isRetakingTest = true;
      questionnaireCompleted = false;
      canShowQuestionnaire = true;
      activeQuestionnaireData = null; // Clear old data to force refresh
      forceProductsVideoOnNextResults = true;
      lastGuidedProductId = null;
      try { localStorage.removeItem(LAST_GUIDED_PRODUCT_STORAGE_KEY); } catch (e) { /* ignore */ }

      // Reset Questionnaire Module
      if (typeof ChatbotQuestionnaire !== 'undefined') {
        ChatbotQuestionnaire.resetQuestionnaire();
      }

      if (typeof ChatbotMessages !== 'undefined' && ChatbotMessages.clearGuidedOptions) {
        ChatbotMessages.clearGuidedOptions();
      }
      if (DataManager && typeof DataManager.clearAllCachedState === 'function') {
        DataManager.clearAllCachedState();
      } else if (DataManager && typeof DataManager.setCachedRecommendations === 'function') {
        // Backward fallback if clearAllCachedState is unavailable.
        DataManager.setCachedRecommendations(null);
      }
      if (window.ChatbotProducts && typeof window.ChatbotProducts.clearRuntimeState === 'function') {
        window.ChatbotProducts.clearRuntimeState();
      }
      if (window.ChatbotProducts && typeof window.ChatbotProducts.syncStorefrontCartCount === 'function') {
        window.ChatbotProducts.syncStorefrontCartCount();
      }

      // Reset View & proceed to questionnaire directly
      if (ViewController) ViewController.resetViewForRetake();
      proceedToQuestionnaire();

      if (DOM.backToResultsBtn) {
        const cached = DataManager ? DataManager.getCachedRecommendations() : null;
        DOM.backToResultsBtn.style.display = cached ? 'block' : 'none';
        DOM.backToResultsBtn.onclick = () => {
          isRetakingTest = false;
          showChatInterface(cached);
        };
      }
    }

    async function proceedToQuestionnaire() {
      console.log("[Chatbot] Proceeding to Questionnaire...");
      stopCamera();

      if (window.ChatbotUI && typeof window.ChatbotUI.setHeaderTitle === 'function') {
        window.ChatbotUI.setHeaderTitle('Decode Your Skin, Find Your Routine');
      }

      // Update UI Transition
      if (DOM.welcomeScreen) DOM.welcomeScreen.style.display = 'none';
      if (DOM.faceScanContainer) DOM.faceScanContainer.style.display = 'none';
      if (DOM.questionnaireContainer) DOM.questionnaireContainer.style.display = 'flex';
      if (DOM.productsContainer) DOM.productsContainer.style.display = 'none';
      if (DOM.messagesContainer) DOM.messagesContainer.style.display = 'none';
      if (DOM.chatInput) DOM.chatInput.style.display = 'none';

      if (typeof ChatbotMessages !== 'undefined') {
        ChatbotMessages.clearMessages();
      }

      questionnaireCompleted = false;
      canShowQuestionnaire = true;

      // ALWAYS fetch fresh data on retake/start to ensure DB changes are picked up
      if (DOM.questionnaireContent && ViewController) {
        DOM.questionnaireContent.innerHTML = ViewController.showSpinner('Loading your personalized questions...');
      }

      // Only fetch if we don't have it yet (from background prefetch) or if it's explicitly null
      if (!activeQuestionnaireData) {
        console.log("[Chatbot] No activeQuestionnaireData found, fetching now...");
        activeQuestionnaireData = await DataManager.loadQuestionnaireData(true);
      } else {
        console.log("[Chatbot] Using pre-fetched activeQuestionnaireData.");
      }
      
      console.log("[Chatbot] activeQuestionnaireData state:", activeQuestionnaireData ? "Present" : "Missing");

      if (!activeQuestionnaireData || !activeQuestionnaireData.questionnaire) {
        console.error("[Chatbot] Questionnaire data is still empty. Aborting.");
        if (ViewController) ViewController.showError(DOM.questionnaireContent, 'Unable to load questions. Please check your connection.', 'Try Again', 'window.location.reload()');
        return;
      }

      // Init Questionnaire Module
      const success = initQuestionnaireModule();
      if (!success) {
        console.error("[Chatbot] Failed to initialize questionnaire module (initQuestionnaireModule returned false)");
        return;
      }

      // Start Questionnaire UI
      setTimeout(() => {
        if (typeof ChatbotQuestionnaire !== 'undefined') {
          console.log("[Chatbot] Calling ChatbotQuestionnaire.initializeQuestionnaire()");
          ChatbotQuestionnaire.initializeQuestionnaire();
          // DO NOT set isRetakingTest = false here; it should stay true until submission or explicit exit
        } else {
          console.error("[Chatbot] ChatbotQuestionnaire is undefined at initialization time");
        }
      }, 50);
    }

    // For each question we need is_multi_select (true = multi-option, false = single option).
    // Use API value when backend sends it; fallback to this map until API exposes the field.
    const QUESTION_META = {
      '0b876749-7236-4954-946a-ac8ee79084a9': { is_multi_select: false, is_mandatory: false },
      '0d357525-61b3-402f-9dad-1e3029730fc5': { is_multi_select: false, is_mandatory: true },
      '102cf6af-2d5f-4cd0-b3f0-2378823b2db8': { is_multi_select: true, is_mandatory: true },
      '16eb3959-f31c-4a66-9cb9-83f744b7ace3': { is_multi_select: false, is_mandatory: false },
      '17e6479d-44e9-4cf5-a525-728eb3f5de4a': { is_multi_select: false, is_mandatory: true },
      '2b7a97ae-5841-46bb-b60e-642251faa157': { is_multi_select: false, is_mandatory: true },
      '30cd5c07-4cbc-4962-abc4-00536ee6463a': { is_multi_select: false, is_mandatory: false },
      '37f5ec2f-436e-4a8e-8cd8-7e756785680f': { is_multi_select: true, is_mandatory: true },
      '46525b3b-801e-4c95-8322-79d0eda4c128': { is_multi_select: false, is_mandatory: false },
      '4a6a456d-b11a-4ea7-964a-2fcb848f7c70': { is_multi_select: false, is_mandatory: false },
      '6ebed7f0-8114-444d-8b48-83fae33e1640': { is_multi_select: false, is_mandatory: true },
      '71dd5bd3-903f-47a6-b3f8-aab20f8bd95f': { is_multi_select: false, is_mandatory: true },
      '8cd6382b-be5d-4bc5-be4f-a653e249d9d8': { is_multi_select: false, is_mandatory: false },
      '8d6a3e5d-6974-491f-b9a2-b0697f607562': { is_multi_select: false, is_mandatory: false },
      'a24bbefc-7297-406b-9e9d-f20fedd50403': { is_multi_select: true, is_mandatory: false },
      'a3e0683d-46bd-4554-8870-9ffc881682f8': { is_multi_select: false, is_mandatory: true },
      'b7d45328-239d-44c3-96ec-6931f5a5ad7e': { is_multi_select: true, is_mandatory: false },
      'ca1fdc67-b8b3-4ae1-82b9-80e45d8203fb': { is_multi_select: false, is_mandatory: false }
    };

    // Helper to init the questionnaire module logic
    function initQuestionnaireModule() {
      console.log("[Chatbot] initQuestionnaireModule called. activeQuestionnaireData:", activeQuestionnaireData);
      if (!activeQuestionnaireData || !activeQuestionnaireData.questionnaire) {
        console.warn("[Chatbot] Missing activeQuestionnaireData or questionnaire array");
        if (ViewController) ViewController.showError(DOM.questionnaireContent, 'Failed to load data', 'Reload', 'location.reload()');
        return false;
      }

      const questionsByOrderIndex = {};
      activeQuestionnaireData.questionnaire.forEach(function (q) {
        if (q && q.order_index !== undefined) {
          var meta = QUESTION_META[q.question_id];
          // Prefer API is_multi_select when present; else use fallback (QUESTION_META).
          var enriched = Object.assign({}, q, {
            is_multi_select: q.is_multi_select !== undefined ? q.is_multi_select : (meta ? meta.is_multi_select : false),
            is_mandatory: q.is_mandatory !== undefined ? q.is_mandatory : (meta ? meta.is_mandatory : false)
          });
          questionsByOrderIndex[q.order_index] = enriched;
        }
      });

      if (typeof ChatbotQuestionnaire !== 'undefined') {
        ChatbotQuestionnaire.setDOMReferences({
          questionnaireContent: DOM.questionnaireContent,
          progressFill: DOM.progressFill,
          currentQuestionSpan: DOM.currentQuestionSpan,
          totalQuestionsSpan: DOM.totalQuestionsSpan,
          nextQuestionBtn: DOM.nextQuestionBtn,
          prevQuestionBtn: DOM.prevQuestionBtn,
          QUESTIONNAIRE_ENDPOINT: CONFIG.ENDPOINTS.SUBMIT_RESULTS,
          CUSTOMER_ID: CUSTOMER_ID,
          CUSTOMER_NAME: CUSTOMER_NAME,
          CUSTOMER_EMAIL: CUSTOMER_EMAIL,
          questionsByOrderIndex,
          questionnaireData: activeQuestionnaireData,
          showChatInterface, // Callback to show results
          messagesContainer: DOM.messagesContainer
        });
        console.log("[Chatbot] Questionnaire references set successfully.");
        return true;
      }
      console.error("[Chatbot] ChatbotQuestionnaire module NOT found in global scope.");
      return false;
    }

    function showChatInterface(recommendations) {
      // Use DataManager to ensure we have the latest
      const cached = DataManager ? DataManager.getCachedRecommendations() : null;
      let recsToShow = recommendations || cached;

      // Update cache if new recs provided
      if (recommendations && DataManager) {
        if (typeof DataManager.setCachedRecommendations === 'function') {
          DataManager.setCachedRecommendations(recommendations);
        }
        recsToShow = recommendations;
      }

      var guidedResume =
        typeof ChatbotMessages !== 'undefined' &&
        typeof ChatbotMessages.hasPersistedGuidedActivity === 'function' &&
        ChatbotMessages.hasPersistedGuidedActivity();
      var shouldForceProductsVideo = forceProductsVideoOnNextResults === true;

      questionnaireCompleted = true; // Assume completed if showing interface
      canShowQuestionnaire = false;  // Ensure we don't steer them back to questions
      stopCamera();

      if (ViewController) {
        ViewController.stopWelcomeVideo();
        if (shouldForceProductsVideo) {
          if (ViewController.setProductsVideoEligible) ViewController.setProductsVideoEligible(true);
          if (ViewController.setProductsVideoShownOnce) ViewController.setProductsVideoShownOnce(false);
        } else if (guidedResume) {
          if (ViewController.setProductsVideoEligible) ViewController.setProductsVideoEligible(false);
          if (ViewController.setProductsVideoShownOnce) ViewController.setProductsVideoShownOnce(true);
        } else {
          if (ViewController.setProductsVideoEligible) ViewController.setProductsVideoEligible(true);
          if (ViewController.setProductsVideoShownOnce) ViewController.setProductsVideoShownOnce(false);
        }
      }

      // Use ViewController to switch view?
      // Actually showChatInterface in old main.js did a lot of layout setup
      // Let's rely on ChatbotProducts to render and then we layout

      if (typeof ChatbotProducts !== 'undefined') {
        try {
          const answers = (typeof ChatbotQuestionnaire !== 'undefined') ? ChatbotQuestionnaire.getAnswers() : [];
          ChatbotProducts.showProductsPage(recsToShow, answers);
        } catch (err) {
          console.error("Error displaying products page:", err);
          // Proceed to show interface anyway (will likely be empty/fallback)
        }
      }

      // Restore last guided product thread after reload / reopen (pairs with localStorage history).
      var lastPid = null;
      try {
        lastPid = localStorage.getItem(LAST_GUIDED_PRODUCT_STORAGE_KEY);
      } catch (e) { /* ignore */ }
      if (lastPid && typeof ChatbotMessages !== 'undefined' && guidedResume && !shouldForceProductsVideo) {
        if (typeof ChatbotMessages.setGuidedProductKey === 'function') {
          ChatbotMessages.setGuidedProductKey(lastPid);
        }
        if (typeof ChatbotMessages.loadHistory === 'function' && ChatbotMessages.loadHistory(lastPid)) {
          if (DOM.chatInput) DOM.chatInput.style.display = 'none';
          lastGuidedProductId = lastPid;
        }
      }
      if (shouldForceProductsVideo) {
        forceProductsVideoOnNextResults = false;
      }

      // Layout: Combined View (Products & Chat)
      if (ViewController) {
        ViewController.toggleResultsView(true);
      }

      // Ensure visibility is managed by ViewController, but we can do a final check
      if (DOM.chatInput) DOM.chatInput.style.display = 'none';
      if (DOM.viewResultsHeaderBtn) DOM.viewResultsHeaderBtn.style.display = 'none';
      if (DOM.backToResultsBtn) DOM.backToResultsBtn.style.display = 'none';

      // FORCE TOP POSITION
      if (DOM.contentContainer) DOM.contentContainer.scrollTop = 0;
    }

    // Face Scan
    function startFaceScan() {
      stopCamera();

      // Hide others
      if (DOM.welcomeScreen) DOM.welcomeScreen.style.display = 'none';
      if (DOM.productsContainer) DOM.productsContainer.style.display = 'none';
      if (DOM.questionnaireContainer) DOM.questionnaireContainer.style.display = 'none';
      if (DOM.chatInput) DOM.chatInput.style.display = 'none';
      if (DOM.messagesContainer) DOM.messagesContainer.style.display = 'none';

      // Show main panel
      const mainPanel = document.getElementById('chatbot-main-panel');
      if (mainPanel) mainPanel.style.display = 'flex';

      if (typeof ChatbotFaceScan !== 'undefined') {
        ChatbotFaceScan.startFaceScan();
      }
    }

    function stopCamera() {
      if (typeof ChatbotFaceScan !== 'undefined') {
        ChatbotFaceScan.stopCamera();
      }
    }

    // ========================================
    // EVENT LISTENERS
    // ========================================
    DOM.toggle.addEventListener("click", toggleChatbot);
    DOM.close.addEventListener("click", () => {
      // Close logic
      if (DOM.box) DOM.box.classList.remove('open');
      if (DOM.toggle) DOM.toggle.classList.remove('hidden');
      // We might need to update ViewController (isOpen state) but since we manipulate DOM directly here...
      // Better to use ViewController.toggleChatbot(false)
      if (ViewController) ViewController.toggleChatbot(false);
      stopCamera();
    });
    if (DOM.expand) {
      DOM.expand.addEventListener("click", () => {
        if (ViewController) ViewController.toggleExpand();
      });
    }

    window.addEventListener('resize', () => {
      if (ViewController) ViewController.updateLayoutForState();
    });

    if (DOM.viewResultsHeaderBtn) {
      DOM.viewResultsHeaderBtn.addEventListener("click", () => {
        if (isRetakingTest) return;
        // Toggle view logic inside ViewController?
        // It depends on current state "isViewingResults"
        // Since we don't expose that easily, let's implement the toggle logic here utilizing VC
        // Actually VC.toggleResultsView handles "switching to results". 
        // We need a toggle.

        // Check if products container is visible
        const productsVisible = DOM.productsContainer && DOM.productsContainer.style.display !== 'none';

        if (productsVisible) {
          if (ViewController) ViewController.toggleChatView();
        } else {
          if (ViewController) ViewController.toggleResultsView(true);
        }
      });
    }

    // Buttons
    // Note: We bind Welcome Screen buttons in showWelcomeScreen.

    // Face Scan Callbacks (background upload so Submit feels instant)
    var pendingFaceScanImage = null;
    var pendingFaceScanPromise = null;

    if (typeof ChatbotFaceScan !== 'undefined') {
      ChatbotFaceScan.setDOMReferences({
        ...DOM, // Spread all DOM refs provided by UI
        // Specific overrides/extras if needed
      });

      ChatbotFaceScan.setCallbacks({
        onImageReady: function (imageBase64) {
          if (!imageBase64 || !DataManager) return;
          pendingFaceScanImage = imageBase64;
          pendingFaceScanPromise = DataManager.uploadFaceScan(imageBase64, USER_INFO)
            .then(function (responseData) {
              if (responseData && DataManager.setCachedFaceAnalysis) {
                DataManager.setCachedFaceAnalysis(responseData);
              }
              return responseData;
            })
            .catch(function (err) {
              console.warn("[Chatbot] Face scan upload failed:", err);
              return null;
            });
        },
        onComplete: async (imageBase64) => {
          if (DataManager) {
            try {
              // Get user ID
              const isGuest = !USER_INFO.id || USER_INFO.id === null || USER_INFO.id === 'null' || USER_INFO.id === '';
              let userShopifyId;

              if (isGuest) {
                try {
                  let guestId = localStorage.getItem('chatbot_guest_id');
                  if (!guestId || isNaN(guestId.replace('guest_', ''))) {
                    guestId = Date.now().toString() + Math.floor(Math.random() * 1000).toString();
                    localStorage.setItem('chatbot_guest_id', guestId);
                  }
                  userShopifyId = guestId.replace('guest_', '');
                } catch (e) {
                  userShopifyId = Date.now().toString();
                }
              } else {
                userShopifyId = USER_INFO.id;
              }

              // Face scan API is called here (get-skin-analysis-from-image). Result drives whether image is "good" or we show retake.
              var faceScanPromise = (pendingFaceScanImage === imageBase64 && pendingFaceScanPromise)
                ? pendingFaceScanPromise
                : DataManager.uploadFaceScan(imageBase64, USER_INFO)
                  .then(function (responseData) {
                    if (responseData && DataManager.setCachedFaceAnalysis) {
                      DataManager.setCachedFaceAnalysis(responseData);
                    }
                    return responseData;
                  })
                  .catch(function (err) {
                    console.warn("[Chatbot] Face scan upload failed:", err);
                    return null;
                  });
              pendingFaceScanImage = null;
              pendingFaceScanPromise = null;

              // Wait for face scan API result — this is what we use to show "image good" or "image not clear"
              const faceScanResult = await faceScanPromise;

              // API failed (network/error) — show retake so user can try again
              if (!faceScanResult) {
                if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.showResolutionError) {
                  ChatbotFaceScan.showResolutionError('Could not analyze image. Please try again or use a clearer photo.');
                }
                if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.resetSubmitButton) {
                  ChatbotFaceScan.resetSubmitButton();
                }
                return;
              }

              // API returned — check if face scan report says image is not clear (StatusCode 101 = resolution/quality issue)
              // Do NOT use top-level Message ("Success") — that only means the API call succeeded. Use Tags to decide.
              var tags = faceScanResult.Tags || faceScanResult.tags;
              var showRetakeFromApi = false;
              var resolutionMessage = 'Image is not clear, upload clear or closer image';

              if (tags && Array.isArray(tags)) {
                // StatusCode can be number 101 or string "101"
                var failedTags = tags.filter(function (t) { return t.StatusCode == 101 || t.StatusCode === '101'; });
                var totalTags = tags.length;
                if (failedTags.length > 0 && totalTags > 0 && failedTags.length / totalTags > 0.4) {
                  showRetakeFromApi = true;
                  // Use first failed tag's Message (e.g. "Unable to process, not enough resolution.") — NOT top-level "Success"
                  var firstFailed = failedTags[0];
                  if (firstFailed && (firstFailed.Message || firstFailed.message)) {
                    var msg = (firstFailed.Message || firstFailed.message).toString().trim();
                    if (msg) resolutionMessage = msg;
                  }
                }
              }

              if (showRetakeFromApi) {
                if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.showResolutionError) {
                  ChatbotFaceScan.showResolutionError(resolutionMessage);
                }
                if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.resetSubmitButton) {
                  ChatbotFaceScan.resetSubmitButton();
                }
                return;
              }

              // Face scan API says image is good — proceed
              if (canShowQuestionnaire) {
                if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.hide) ChatbotFaceScan.hide();
                proceedToQuestionnaire();
                return;
              }

              // Only for returning users: face scan already done (faceScanResult), fetch recommendations
              if (ViewController) ViewController.showToast("Fetching your recommendations...", "info");

              const endpoint = (CONFIG && CONFIG.ENDPOINTS && CONFIG.ENDPOINTS.GET_EXISTING_RECOMMENDATIONS)
                || 'https://codeskin-service-625951850094.asia-south1.run.app/tools/get-existing-recommended-products';

              let products = null;
              if (window.ChatbotAPI) {
                products = await window.ChatbotAPI.post(`${endpoint}?user_shopify_id=${userShopifyId}`, {});
              } else {
                const response = await fetch(`${endpoint}?user_shopify_id=${userShopifyId}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({})
                });
                products = await response.json();
              }

              // Hide scanner ONLY after we have results or decide to show interface
              if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.hide) ChatbotFaceScan.hide();

              if (products) {
                DataManager.setCachedRecommendations(products);
                showChatInterface(products);
              } else {
                showChatInterface(null);
              }

            } catch (e) {
              console.error("Face scan fetch error:", e);
              if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.hide) ChatbotFaceScan.hide();
              if (ViewController) ViewController.showToast("Unable to load recommendations", "error");
              showChatInterface(null);
            }
          }
        },
        onCancel: () => {
          if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.hide) ChatbotFaceScan.hide();
          // During retake flow, skip must always continue to fresh questionnaire.
          if (isRetakingTest) {
            proceedToQuestionnaire();
            return;
          }
          // If already completed OR if they are a returning user who shouldn't see questionnaire
          if (questionnaireCompleted || !canShowQuestionnaire) {
            showChatInterface(DataManager ? DataManager.getCachedRecommendations() : null);
          } else {
            proceedToQuestionnaire();
          }
        }
      });
    }

    // Messages Module
    if (typeof ChatbotMessages !== 'undefined') {
      DOM.sendBtn.addEventListener("click", () => {
        // Use questionnaire answers if available
        const answers = (typeof ChatbotQuestionnaire !== 'undefined') ? ChatbotQuestionnaire.getAnswers() : [];
        ChatbotMessages.sendMessage(answers);
      });

      DOM.input.addEventListener("keypress", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          DOM.sendBtn.click();
        }
      });

      ChatbotMessages.setDOMReferences({
        messagesContainer: DOM.messagesContainer,
        input: DOM.input,
        sendBtn: DOM.sendBtn,
        CUSTOMER_ID,
        CUSTOMER_EMAIL,
        CUSTOMER_NAME,
        API_ENDPOINT: CONFIG.ENDPOINTS.CHAT
      });
    }

    // Products Module
    if (typeof ChatbotProducts !== 'undefined') {
      ChatbotProducts.setDOMReferences({
        productsContainer: DOM.productsContainer,
        chatbotBox: DOM.box,
        onRetake: retakeTest,
        onRetakeQuestionnaire: retakeQuestionnaire,
        onFaceScan: startFaceScan,
        onProductClick: (product, card) => {
          if (productClickInProgress) return;
          productClickInProgress = true;
          setTimeout(function () { productClickInProgress = false; }, 450);

          var productName = product.product_name || product.product || product.title || product.name || 'this product';

          // Root-cause fix: if a previous FAQ answer is still loading, cancel it before switching product context.
          if (ChatbotMessages && typeof ChatbotMessages.cancelPendingResponse === 'function') {
            ChatbotMessages.cancelPendingResponse();
          }

          // Persist the *previous* guided product's transcript + which FAQs were already asked.
          // MUST run before setCurrentProduct: setCurrentProduct swaps askedQuestions for the next product,
          // so saving after it stored the wrong Set and broke disabled chips when returning to a product.
          if (ChatbotMessages && typeof ChatbotMessages.saveHistory === 'function' && lastGuidedProductId) {
            ChatbotMessages.saveHistory(lastGuidedProductId);
          }

          // STEP 0: Set product context so chat API knows which product user is asking about
          if (ChatbotMessages && ChatbotMessages.setCurrentProduct) {
            ChatbotMessages.setCurrentProduct(product);
          }

          // STEP 1: Mark products intro video as consumed BEFORE view switch.
          // Root cause fix: showGuidedChatView can recreate/play the video when this flag is still false.
          if (ViewController && ViewController.setProductsVideoShownOnce) {
            ViewController.setProductsVideoShownOnce(true);
          }
          if (ViewController && ViewController.setProductsVideoEligible) {
            ViewController.setProductsVideoEligible(false);
          }
          if (ViewController && ViewController.stopAllChatbotMedia) {
            ViewController.stopAllChatbotMedia();
          }

          // STEP 2: Show guided chat view so messages area is visible and laid out before we append
          if (ViewController && ViewController.showGuidedChatView) {
            ViewController.showGuidedChatView();
          }

          // STEP 3: Hard-remove product intro video when user clicks a product
          // so FAQs/quick replies are the only visible content in guided chat.
          var chatbotBox = document.getElementById('chatbot-box');
          if (chatbotBox) {
            chatbotBox.querySelectorAll('video, audio').forEach(function (m) {
              try {
                m.pause();
                m.currentTime = 0;
                m.muted = true;
                if (m.dataset) m.dataset.chatbotStopped = '1';
              } catch (e) { /* ignore */ }
            });
            chatbotBox.querySelectorAll('.products-video-section').forEach(function (node) {
              try { node.remove(); } catch (e) { /* ignore */ }
            });
          }

          // STEP 4: Center parent category card so animation aligns with card, then highlight
          var categoryCard = card && card.closest('.category-parent-card');
          var carousel = card && card.closest('.mobile-products-unified-container');
          if (categoryCard && carousel) {
            var scrollX = categoryCard.offsetLeft - (carousel.offsetWidth / 2) + (categoryCard.offsetWidth / 2);
            carousel.scrollTo({ left: scrollX, behavior: 'smooth' });
          }
          if (card && card.classList) {
            card.classList.add('product-card-peak');
            setTimeout(function () { card.classList.remove('product-card-peak'); }, 500);
          }

          // STEP 5: Add guided content immediately – no delay for instant response
          function addGuidedContent() {
            if (!ChatbotMessages) return;
            // saveHistory(lastGuidedProductId) runs earlier in onProductClick (before setCurrentProduct).
            // Unique prodId per product – critical so AM vs PM (or same product, different usage) each get their own guided chat
            var prodId = product.shopify_product_id || product.id || product.product_id || product.handle;
            if (product.variant_id || product.shopify_variant_id) {
              prodId = (prodId || 'p') + '_v_' + (product.variant_id || product.shopify_variant_id);
            } else {
              // Differentiate AM/PM or same product with different application_time
              var timeKey = (product.application_time || product.usage_time || product.recommended_time || product.time_of_use || '').toString().replace(/\s+/g, '_');
              if (timeKey && prodId) {
                prodId = (prodId || 'p') + '_t_' + timeKey;
              } else {
                var imgUrl = (product.image_url || (product.image && (product.image.src || product.image.url)) || '').toString();
                if (imgUrl && prodId) {
                  var h = 0;
                  for (var j = 0; j < Math.min(imgUrl.length, 80); j++) h = ((h << 5) - h) + imgUrl.charCodeAt(j) | 0;
                  prodId = prodId + '_i' + Math.abs(h).toString(36);
                }
              }
            }
            if (!prodId) {
              var name = (product.product_name || product.product || product.title || product.name || 'p').toString().replace(/\s+/g, '_');
              var extra = (product.variant_id || product.shopify_variant_id || '').toString() ||
                ((product.image_url || (product.image && (product.image.src || product.image.url)) || '').toString().slice(-24));
              var hash = 0;
              try {
                var str = JSON.stringify({ n: name, e: extra, i: product.image_url });
                for (var i = 0; i < Math.min(str.length, 150); i++) hash = ((hash << 5) - hash) + str.charCodeAt(i) | 0;
              } catch (e) { hash = name.length * 31; }
              prodId = '_n_' + name + '_' + (extra || Math.abs(hash).toString(36));
            }
            prodId = String(prodId);
            // Align FAQ asked-state with the exact history prodId used below.
            if (ChatbotMessages && typeof ChatbotMessages.setGuidedProductKey === 'function') {
              ChatbotMessages.setGuidedProductKey(prodId);
            }
            var hasHistory = ChatbotMessages.loadHistory(prodId);
            lastGuidedProductId = prodId;
            try {
              localStorage.setItem(LAST_GUIDED_PRODUCT_STORAGE_KEY, prodId);
            } catch (e) { /* ignore */ }

            // Always compute the "right" questions for the clicked product.
            // Critical UX rule: if backend provides NO product questions, DO NOT show generic defaults.
            // Instead show only "My question is not listed above" so the user can ask freely.
            var rawQuestions = (Array.isArray(product.chat_questions) && product.chat_questions.length > 0)
              ? product.chat_questions
              : ((Array.isArray(product.guided_questions) && product.guided_questions.length > 0) ? product.guided_questions : []);

            // Normalize strings and remove empty entries
            var questions = rawQuestions
              .map(function (q) { return q == null ? '' : String(q).trim(); })
              .filter(function (q) { return q.length > 0; });

            var hasCustomQuestion = questions.indexOf('My question is not listed above') !== -1;
            var shouldShowOnlyCustom = questions.length === 0;

            if (shouldShowOnlyCustom) {
              questions = ['My question is not listed above'];
            } else if (!hasCustomQuestion) {
              // Still allow users to ask something not listed by backend.
              questions.push('My question is not listed above');
            }

            // Update guided options + quick replies to match *this* clicked product,
            // even if chat history was loaded.
            ChatbotMessages.setGuidedOptions(questions);

            // Replace existing quick replies to avoid showing options from older products.
            try {
              var replies = DOM.messagesContainer && DOM.messagesContainer.querySelector('.quick-replies-container');
              if (replies) replies.remove();
            } catch (e) { /* ignore */ }

            if (hasHistory) {
              requestAnimationFrame(function () {
                ChatbotMessages.scrollToTop(true);
                // Keep viewport at top for previous products too; don't auto-jump to bottom.
                ChatbotMessages.addQuickReplies(questions, false);
              });
            } else {
              ChatbotMessages.clearMessages();
              ChatbotMessages.addMessage('Great! ' + productName + ' is an excellent choice. What would you like to know about it?', false, false);
              ChatbotMessages.addQuickReplies(questions, false);
              requestAnimationFrame(function () { ChatbotMessages.scrollToTop(true); });
            }
            if (DOM.chatInput) DOM.chatInput.style.display = 'none';
          }

          addGuidedContent();
        }
      });
    }

    // Kickoff
    loadDataAndInit();

    // Attach Start Button Listener initially
    const startBtn = document.getElementById("start-questionnaire-btn");
    if (startBtn) startBtn.onclick = startFaceScan;

  });

})();
